import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navLinks, logoUrl, contactData } from '../data/mock';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  // Pages with light backgrounds need the solid (dark-text) header from the start
  const LIGHT_BG_ROUTES = ['/partner-with-us', '/board-interest', '/petition', '/storybook-subscribe', '/partners/', '/professional-development/play-science-institute/register', '/accessibility', '/board/'];
  const onLightPage = LIGHT_BG_ROUTES.some((r) => location.pathname.startsWith(r));
  const isScrolled = scrolled || onLightPage;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link) => {
    if (link.dropdown) {
      setOpenDropdown(openDropdown === link.name ? null : link.name);
      return;
    }
    
    if (link.href.startsWith('#')) {
      if (!isHomePage) {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(link.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(link.href);
    }
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownItemClick = (href) => {
    if (href.startsWith('http')) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else if (href.startsWith('#')) {
      // Anchor link — scroll to section on the home page
      if (!isHomePage) {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={handleLogoClick}
            role="link"
            tabIndex={0}
            aria-label="DC Early Learning Collaborative — home"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLogoClick(); } }}
          >
            <img 
              src={logoUrl} 
              alt="DC Early Learning Collaborative" 
              className="w-14 h-14 rounded-full object-cover shadow-lg"
            />
            <div className="hidden sm:block">
              <p className={`font-bold text-lg leading-tight transition-colors duration-300 ${
                isScrolled ? 'text-slate-800' : 'text-white'
              }`}>
                DCELC
              </p>
              <p className={`text-xs leading-tight transition-colors duration-300 ${
                isScrolled ? 'text-slate-600' : 'text-white/80'
              }`}>
                DC Early Learning Collaborative
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.dropdown ? (
                  <div className="flex items-center">
                    {link.href && link.href !== '#' ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(link.href);
                            setOpenDropdown(null);
                          }}
                          data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`text-sm font-medium transition-all duration-300 hover:scale-105 ${
                            isScrolled
                              ? 'text-slate-700 hover:text-teal-600'
                              : 'text-white/90 hover:text-white'
                          }`}
                        >
                          {link.name}
                        </button>
                        <button
                          aria-expanded={openDropdown === link.name}
                          aria-haspopup="true"
                          aria-label={`Open ${link.name} menu`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === link.name ? null : link.name);
                          }}
                          className={`ml-1 p-0.5 transition-all duration-300 ${
                            isScrolled
                              ? 'text-slate-700 hover:text-teal-600'
                              : 'text-white/90 hover:text-white'
                          }`}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                        </button>
                      </>
                    ) : (
                      <button
                        aria-expanded={openDropdown === link.name}
                        aria-haspopup="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(openDropdown === link.name ? null : link.name);
                        }}
                        className={`flex items-center gap-1 text-sm font-medium transition-all duration-300 hover:scale-105 ${
                          isScrolled
                            ? 'text-slate-700 hover:text-teal-600'
                            : 'text-white/90 hover:text-white'
                        }`}
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    
                    {/* Dropdown Menu */}
                    {openDropdown === link.name && (
                      <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50">
                        {link.items.map((item) => (
                          <button
                            key={item.name}
                            onClick={() => handleDropdownItemClick(item.href)}
                            className={`w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-600 transition-colors duration-200 ${
                              item.indent ? 'pl-8 border-t border-slate-100 text-slate-500 italic' : ''
                            }`}
                          >
                            {item.indent && <span className="mr-1">↳</span>}
                            {item.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavClick(link)}
                    className={`text-sm font-medium transition-all duration-300 hover:scale-105 ${
                      isScrolled
                        ? 'text-slate-700 hover:text-teal-600'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </button>
                )}
              </div>
            ))}
            
            {/* Language Selector */}
            <LanguageSelector isScrolled={isScrolled} />
            
            <Button
              onClick={() => navigate('/newsletter')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="header-newsletter-desktop"
            >
              Newsletter
            </Button>

            <Button
              onClick={() => navigate('/partner-with-us')}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="header-partner-with-us-desktop"
            >
              Partner With Us
            </Button>
            
            <a
              href="https://www.zeffy.com/en-US/donation-form/help-build-an-equitable-accessible-and-affordable-early-childhood-system-in-the-district-of-columbia"
              target="_blank"
              rel="noopener noreferrer"
              data-donate-link="true"
              aria-label="Donate to DC Early Learning Collaborative (opens in a new tab)"
              className="inline-flex items-center justify-center px-5 py-2 bg-[#079897] hover:bg-[#067a79] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Donate
            </a>

            <div className="flex items-center gap-1 ml-1" data-testid="header-social-links-desktop">
              {[
                { icon: Facebook, href: contactData.socialMedia.facebook, label: 'Facebook' },
                { icon: Instagram, href: contactData.socialMedia.instagram, label: 'Instagram' },
                { icon: Youtube, href: contactData.socialMedia.youtube, label: 'YouTube' },
                { icon: Linkedin, href: contactData.socialMedia.linkedin, label: 'LinkedIn' }
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  data-testid={`header-social-${social.label.toLowerCase()}-desktop`}
                  className={`p-1.5 rounded-full transition-all duration-300 hover:scale-110 ${
                    isScrolled ? 'text-slate-600 hover:text-[#079897] hover:bg-slate-100' : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </nav>
          <button
            className="lg:hidden p-2 rounded-lg transition-colors duration-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? 'text-slate-800' : 'text-white'} size={24} />
            ) : (
              <Menu className={isScrolled ? 'text-slate-800' : 'text-white'} size={24} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[600px] pb-6 visible' : 'max-h-0 invisible'
          }`}
          aria-hidden={!isMobileMenuOpen}
        >
          <nav className="flex flex-col gap-2 pt-4" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.dropdown ? (
                  <div>
                    <div className="flex items-center justify-between">
                      {link.href && link.href !== '#' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(link.href);
                            setIsMobileMenuOpen(false);
                            setOpenDropdown(null);
                          }}
                          className={`flex-1 text-left text-sm font-medium py-2 transition-colors duration-300 ${
                            isScrolled
                              ? 'text-slate-700 hover:text-teal-600'
                              : 'text-white/90 hover:text-white'
                          }`}
                        >
                          {link.name}
                        </button>
                      ) : (
                        <span className={`flex-1 text-left text-sm font-medium py-2 ${
                          isScrolled ? 'text-slate-700' : 'text-white/90'
                        }`}>
                          {link.name}
                        </span>
                      )}
                      <button
                        aria-expanded={openDropdown === link.name}
                        aria-haspopup="true"
                        aria-label={`Open ${link.name} menu`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(openDropdown === link.name ? null : link.name);
                        }}
                        className={`p-2 transition-colors duration-300 ${
                          isScrolled
                            ? 'text-slate-700 hover:text-teal-600'
                            : 'text-white/90 hover:text-white'
                        }`}
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    
                    {/* Mobile Dropdown */}
                    {openDropdown === link.name && (
                      <div className="pl-4 mt-2 space-y-2 border-l-2 border-teal-200">
                        {link.items.map((item) => (
                          <button
                            key={item.name}
                            onClick={() => handleDropdownItemClick(item.href)}
                            className={`w-full text-left text-sm py-2 transition-colors duration-200 ${
                              isScrolled
                                ? 'text-slate-600 hover:text-teal-600'
                                : 'text-white/80 hover:text-white'
                            } ${item.indent ? 'pl-4 italic' : ''}`}
                          >
                            {item.indent && <span className="mr-1">↳</span>}
                            {item.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavClick(link)}
                    className={`w-full text-left text-sm font-medium py-2 transition-colors duration-300 ${
                      isScrolled
                        ? 'text-slate-700 hover:text-teal-600'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </button>
                )}
              </div>
            ))}
            
            {/* Mobile Language Selector */}
            <div className="py-2">
              <LanguageSelector isScrolled={isScrolled} />
            </div>
            
            <Button
              onClick={() => { setIsMobileMenuOpen(false); navigate('/newsletter'); }}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white w-full mt-2"
              data-testid="header-newsletter-mobile"
            >
              Newsletter
            </Button>

            <Button
              onClick={() => { setIsMobileMenuOpen(false); navigate('/partner-with-us'); }}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white w-full mt-2"
              data-testid="header-partner-with-us-mobile"
            >
              Partner With Us
            </Button>
            
            <a
              href="https://www.zeffy.com/en-US/donation-form/help-build-an-equitable-accessible-and-affordable-early-childhood-system-in-the-district-of-columbia"
              target="_blank"
              rel="noopener noreferrer"
              data-donate-link="true"
              aria-label="Donate to DC Early Learning Collaborative (opens in a new tab)"
              className="flex items-center justify-center w-full mt-2 px-5 py-2 bg-[#079897] hover:bg-[#067a79] text-white font-semibold rounded-lg shadow-lg transition-all duration-300"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Donate
            </a>

            <div className="flex items-center justify-center gap-3 mt-4 pb-2" data-testid="header-social-links-mobile">
              {[
                { icon: Facebook, href: contactData.socialMedia.facebook, label: 'Facebook' },
                { icon: Instagram, href: contactData.socialMedia.instagram, label: 'Instagram' },
                { icon: Youtube, href: contactData.socialMedia.youtube, label: 'YouTube' },
                { icon: Linkedin, href: contactData.socialMedia.linkedin, label: 'LinkedIn' }
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  data-testid={`header-social-${social.label.toLowerCase()}-mobile`}
                  className="p-2 rounded-full text-slate-600 hover:text-[#079897] hover:bg-slate-100 transition-all duration-300"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
